export const CACHE_SCHEMA_VERSION = 2;
export const MAX_COUNTED_REALTIME_IDS_PER_DM = 200;

const DELIVERY_RANK = Object.freeze({ sent: 1, delivered: 2, seen: 3 });

export const getDeliveryRank = (status) => DELIVERY_RANK[status] || 0;

export const getMonotonicDeliveryPatch = (existingMessage, incomingMessage) => {
  const existingStatus = existingMessage?.status;
  const incomingStatus = incomingMessage?.status;
  const status = getDeliveryRank(incomingStatus) >= getDeliveryRank(existingStatus)
    ? (getDeliveryRank(incomingStatus) ? incomingStatus : (getDeliveryRank(existingStatus) ? existingStatus : 'sent'))
    : existingStatus;

  const patch = { status };
  if (existingMessage?.delivered_at || incomingMessage?.delivered_at) {
    patch.delivered_at = incomingMessage?.delivered_at || existingMessage?.delivered_at;
  }
  if (status === 'seen' && (existingMessage?.seen_at || incomingMessage?.seen_at)) {
    patch.seen_at = incomingMessage?.seen_at || existingMessage?.seen_at;
  }
  return patch;
};

export const normalizeBoundary = (value) => {
  if (!value) return null;
  const createdAt = value.last_read_created_at || value.created_at;
  const messageId = value.last_read_message_id || value.message_id || value.id;
  if (!createdAt || !messageId) return null;
  return {
    last_read_created_at: createdAt,
    last_read_message_id: String(messageId),
    ...((value.last_read_at || value.read_at) ? { last_read_at: value.last_read_at || value.read_at } : {}),
  };
};

export const compareBoundaries = (leftValue, rightValue) => {
  const left = normalizeBoundary(leftValue);
  const right = normalizeBoundary(rightValue);
  if (!left && !right) return 0;
  if (!left) return -1;
  if (!right) return 1;
  if (left.last_read_created_at !== right.last_read_created_at) {
    return left.last_read_created_at < right.last_read_created_at ? -1 : 1;
  }
  if (left.last_read_message_id === right.last_read_message_id) return 0;
  return left.last_read_message_id < right.last_read_message_id ? -1 : 1;
};

export const advanceBoundary = (existing, candidate) => (
  compareBoundaries(candidate, existing) > 0 ? normalizeBoundary(candidate) : normalizeBoundary(existing)
);

export const messageIsAtOrBeforeBoundary = (message, boundaryValue) => {
  if (!message?.created_at || !message?.id) return false;
  return compareBoundaries({
    last_read_created_at: message.created_at,
    last_read_message_id: message.id,
  }, boundaryValue) <= 0;
};

export const encodeCacheEnvelope = (data) => JSON.stringify({
  schema_version: CACHE_SCHEMA_VERSION,
  data,
});

export const decodeCacheEnvelope = (raw) => {
  if (!raw) return { data: [], migrated: false, valid: true };
  try {
    const parsed = JSON.parse(raw);
    if (Array.isArray(parsed)) return { data: parsed, migrated: true, valid: true };
    if (parsed?.schema_version === CACHE_SCHEMA_VERSION && Array.isArray(parsed.data)) {
      return { data: parsed.data, migrated: false, valid: true };
    }
    return { data: [], migrated: false, valid: false };
  } catch {
    return { data: [], migrated: false, valid: false };
  }
};

export const rememberBoundedRealtimeId = (map, messageId, value, limit = MAX_COUNTED_REALTIME_IDS_PER_DM) => {
  if (!(map instanceof Map) || !messageId) return false;
  const id = String(messageId);
  if (map.has(id)) return false;
  map.set(id, value);
  while (map.size > limit) map.delete(map.keys().next().value);
  return true;
};
